import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatDate } from '../../utils/formatters.js';

const STATUS_VARIANT = { OPEN: 'warning', IN_PROGRESS: 'info', RESOLVED: 'success', CLOSED: 'neutral' };
const PRIORITY_VARIANT = { LOW: 'neutral', MEDIUM: 'info', HIGH: 'warning', URGENT: 'danger' };
const NEXT = {
  OPEN: ['IN_PROGRESS', 'RESOLVED', 'CLOSED'],
  IN_PROGRESS: ['RESOLVED', 'OPEN', 'CLOSED'],
  RESOLVED: ['CLOSED', 'IN_PROGRESS'],
  CLOSED: ['OPEN'],
};

export function TicketDetailModal({ ticketId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['sales', 'ticket', ticketId], queryFn: () => salesApi.getTicket(ticketId).then((r) => r.items), enabled: Boolean(ticketId) });
  const ticket = detail.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sales', 'tickets'] });
    queryClient.invalidateQueries({ queryKey: ['sales', 'ticket', ticketId] });
  };

  const setStatus = useMutation({ mutationFn: (status) => salesApi.setTicketStatus(ticketId, status), onSuccess: invalidate, onError: (e) => setError(e?.message) });
  const addComment = useMutation({ mutationFn: (body) => salesApi.addTicketComment(ticketId, body), onSuccess: () => { setComment(''); invalidate(); }, onError: (e) => setError(e?.message) });

  return (
    <Modal
      open={Boolean(ticketId)}
      onClose={onClose}
      title={ticket ? `${ticket.ticketNumber} · ${ticket.subject}` : t('tk.detail.title')}
      footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}
    >
      {ticket && (
        <div className="form-stack">
          <p className="muted text-sm" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={STATUS_VARIANT[ticket.status]}>{t(`tk.status.${ticket.status}`)}</Badge>
            <Badge variant={PRIORITY_VARIANT[ticket.priority]}>{t(`tk.priority.${ticket.priority}`)}</Badge>
            {ticket.customerName}
          </p>
          {ticket.description && <p>{ticket.description}</p>}

          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {(NEXT[ticket.status] || []).map((s) => (
              <Button key={s} size="sm" variant={s === 'RESOLVED' || s === 'CLOSED' ? 'primary' : 'secondary'}
                disabled={setStatus.isPending} onClick={() => setStatus.mutate(s)}>
                {t(`tk.move.${s}`)}
              </Button>
            ))}
          </div>

          <div className="settings-divider">{t('tk.comments')}</div>
          {(ticket.comments || []).length === 0 && <p className="muted text-sm">{t('tk.noComments')}</p>}
          {(ticket.comments || []).map((c) => (
            <div key={c.id} className="card" style={{ padding: 'var(--space-2) var(--space-3)' }}>
              <p style={{ margin: 0 }}>{c.body}</p>
              <span className="muted text-sm">{formatDate(c.createdAt)}</span>
            </div>
          ))}
          <div className="field-row">
            <input className="input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('tk.addComment')} />
            <Button variant="secondary" disabled={addComment.isPending || !comment.trim()} onClick={() => addComment.mutate(comment.trim())}>{t('tk.send')}</Button>
          </div>
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
